<template>
  <div class="container-fluid px-4 mt-4">
    <div class="d-flex align-items-center justify-content-between mb-3">
      <h5 class="fw-bold mb-0">정기권 교체 관리</h5>
      <span class="text-muted small" v-if="selectedArea && readyForExchange.length > 0">
        교체 가능: {{ readyForExchange.length }}명
      </span>
    </div>

    <!-- 주차장 선택 -->
    <div class="card shadow-sm mb-4">
      <div class="card-body p-4">
        <h6 class="fw-bold mb-3">주차장 선택</h6>
        <div class="mb-3">
          <select v-model="selectedArea" class="form-select" @change="fetchCompletedPayments">
            <option value="">-- 주차장 선택 --</option>
            <option v-for="a in areas" :key="a.AreaSeq" :value="a.AreaSeq">
              {{ a.AreaName }} ({{ a.AreaCode }})
            </option>
          </select>
        </div>
      </div>
    </div>

    <!-- 날짜 선택 및 교체 (상단) -->
    <template v-if="selectedArea">
      <div v-if="loadError" class="alert alert-danger">{{ loadError }}</div>
      <div v-if="loading" class="text-center py-5 text-muted">불러오는 중...</div>

      <div class="card shadow-sm mb-4">
        <div class="card-body p-4">
          <h6 class="fw-bold mb-3">정기권 기간 설정</h6>

          <div class="row g-3">
            <div class="col-md-6">
              <label class="form-label">시작 날짜 <span class="text-danger">*</span></label>
              <input 
                v-model="exchangeForm.startDate" 
                type="date" 
                class="form-control"
              />
            </div>
            <div class="col-md-6">
              <label class="form-label">종료 날짜 <span class="text-danger">*</span></label>
              <input 
                v-model="exchangeForm.endDate" 
                type="date" 
                class="form-control"
              />
            </div>
          </div>

          <div v-if="exchangeError" class="alert alert-danger py-2 mt-3">{{ exchangeError }}</div>
          <div v-if="exchangeSuccess" class="alert alert-success py-2 mt-3">{{ exchangeSuccess }}</div>

          <div v-if="exchangeProgress.total > 0" class="mt-3">
            <div class="d-flex justify-content-between mb-2">
              <span>진행 상황</span>
              <span>{{ exchangeProgress.current }} / {{ exchangeProgress.total }}</span>
            </div>
            <div class="progress">
              <div
                class="progress-bar bg-info"
                role="progressbar"
                :style="{ width: exchangeProgress.percentage + '%' }"
                :aria-valuenow="exchangeProgress.percentage"
                aria-valuemin="0"
                aria-valuemax="100"
              >
                {{ exchangeProgress.percentage }}%
              </div>
            </div>
          </div>

          <button
            class="btn btn-primary w-100 mt-4"
            :disabled="!exchangeForm.startDate || !exchangeForm.endDate || exchangeProcessing || readyForExchange.length === 0"
            @click="handleExchange"
          >
            {{ exchangeProcessing ? '처리 중...' : `정기권 교체 (${readyForExchange.length}명)` }}
          </button>
        </div>
      </div>

      <!-- 결제 완료 차량 리스트 (아래로 이동) -->
      <div class="card shadow-sm mb-4">
        <div class="card-body p-4">
          <h6 class="fw-bold mb-3">결제 완료 차량 ({{ readyForExchange.length }}명)</h6>
          
          <div v-if="readyForExchange.length === 0" class="text-center text-muted py-4">
            결제 완료된 차량이 없습니다.
          </div>

          <div v-else class="table-responsive">
            <table class="table table-hover align-middle table-sm">
              <thead class="table-dark">
                <tr>
                  <th style="width: 60px">#</th>
                  <th>사용자 ID</th>
                  <th>이름</th>
                  <th>차량번호</th>
                  <th>요금제</th>
                  <th>할인</th>
                  <th>상태</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="w in readyForExchange" :key="w.WaitingSeq">
                  <td class="text-muted small">{{ w.WaitingSeq }}</td>
                  <td class="fw-500">{{ w.UserID }}</td>
                  <td>{{ w.UserName }}</td>
                  <td class="font-monospace small">{{ w.CarNumber }}</td>
                  <td>{{ w.FeeName }}</td>
                  <td>{{ w.DiscountName || '없음' }}</td>
                  <td>
                    <span class="badge bg-success" style="font-size:0.75rem;">결제완료</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </template>

    <!-- 주차장 미선택 안내 -->
    <div v-if="!selectedArea" class="text-center text-muted py-5">
      주차장을 선택하면 결제 완료된 차량 목록이 표시됩니다.
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'AdminParkingPassChangeView',
  data() {
    return {
      areas: [],
      selectedArea: '',
      passings: [],
      loading: false,
      loadError: null,
      exchangeForm: {
        startDate: '',
        endDate: ''
      },
      exchangeError: null,
      exchangeSuccess: null,
      exchangeProgress: { current: 0, total: 0, percentage: 0 },
      exchangeProcessing: false
    }
  },
  computed: {
    readyForExchange() {
      // Status = 'PAYMENT_COMPLTETED'인 차량들만 교체 대상
      return this.passings.filter(p => p.Status === 'PAYMENT_COMPLTETED')
    }
  },
  mounted() {
    this.initializeAreas()
  },
  methods: {
    async initializeAreas() {
      await this.fetchAreas()
      if (this.areas.length > 0) {
        this.selectedArea = this.areas[0].AreaSeq
        await this.fetchCompletedPayments()
      }
    },
    async fetchAreas() {
      try {
        const res = await axios.get(`${process.env.VUE_APP_API_BASE}/api/parking-areas`)
        this.areas = res.data
      } catch (e) {
        console.error('fetchAreas error:', e)
      }
    },
    async fetchCompletedPayments() {
      if (!this.selectedArea) {
        this.passings = []
        return
      }

      this.loading = true
      this.loadError = null
      this.passings = []

      try {
        const res = await axios.get(
          `${process.env.VUE_APP_API_BASE}/api/admin/parking-pass-waiting?areaSeq=${this.selectedArea}`
        )
        this.passings = res.data || []
      } catch (e) {
        this.loadError = '차량 목록을 불러오지 못했습니다.'
        console.error('fetchCompletedPayments error:', e)
      } finally {
        this.loading = false
      }
    },
    async handleExchange() {
      this.exchangeError = null
      this.exchangeSuccess = null

      if (!this.exchangeForm.startDate || !this.exchangeForm.endDate) {
        this.exchangeError = '시작 날짜와 종료 날짜를 모두 선택하세요.'
        return
      }

      if (new Date(this.exchangeForm.startDate) >= new Date(this.exchangeForm.endDate)) {
        this.exchangeError = '종료 날짜가 시작 날짜보다 뒤여야 합니다.'
        return
      }

      if (!confirm(`${this.readyForExchange.length}명의 차량을 정기권으로 교체하시겠습니까?`)) {
        return
      }

      this.exchangeProcessing = true
      this.exchangeProgress.total = this.readyForExchange.length
      this.exchangeProgress.current = 0
      this.exchangeProgress.percentage = 0

      try {
        // 지금은 실제 교체는 안하고, console.log + 진행바만 시뮬레이션
        const exchangeData = {
          areaSeq: parseInt(this.selectedArea),
          startDate: this.exchangeForm.startDate,
          endDate: this.exchangeForm.endDate,
          waitingRecords: this.readyForExchange.map(w => ({
            WaitingSeq: w.WaitingSeq,
            UserSeq: w.UserSeq,
            FeeSeq: w.FeeSeq,
            DiscountSeq: w.DiscountSeq
          }))
        }

        console.log('=== 정기권 교체 데이터 ===')
        console.log(JSON.stringify(exchangeData, null, 2))
        console.log('======================')


        // call backend to perform exchange
        const res = await axios.post(`${process.env.VUE_APP_API_BASE}/api/admin/parking-pass/exchange`, exchangeData)
        const deleted = res.data?.deleted || 0
        const inserted = res.data?.inserted || 0

        // show progress quickly
        for (let i = 0; i < this.readyForExchange.length; i++) {
          await new Promise(resolve => setTimeout(resolve, 80))
          this.exchangeProgress.current = i + 1
          this.exchangeProgress.percentage = Math.round(((i + 1) / this.readyForExchange.length) * 100)
        }

        this.exchangeSuccess = `✓ 기존 ${deleted}건 삭제, 새로 ${inserted}건 추가되어 교체가 완료되었습니다.`

        // 2초 후 데이터 새로고침
        setTimeout(() => {
          this.fetchCompletedPayments()
          this.exchangeForm.startDate = ''
          this.exchangeForm.endDate = ''
        }, 1500)
      } catch (e) {
        this.exchangeError = e.response?.data?.message || '교체 중 오류가 발생했습니다.'
        console.error('handleExchange error:', e)
      } finally {
        this.exchangeProcessing = false
        setTimeout(() => {
          this.exchangeProgress.total = 0
          this.exchangeProgress.current = 0
          this.exchangeProgress.percentage = 0
        }, 2000)
      }
    }
  }
}
</script>

<style scoped>
.table {
  font-size: 0.875rem;
}

.fw-500 {
  font-weight: 500;
}

.font-monospace {
  font-family: 'Courier New', monospace;
}

.progress {
  height: 25px;
}

.progress-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: white;
  font-size: 0.875rem;
}
</style>
